import { Alert, Button, EmptyState, Skeleton } from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import type { JSX } from 'react';

import { getOrderHistory, type OrderHistoryEntry } from '../../../orders/api';

export interface OrderHistoryTabProps {
  orderId: string;
}

const ACTION_LABELS: Record<OrderHistoryEntry['action'], string> = {
  CREATE: 'Creación',
  UPDATE: 'Actualización',
  DELETE: 'Eliminación',
  STATUS_CHANGE: 'Cambio de estado',
  LOGIN: 'Inicio de sesión',
  EXPORT: 'Exportación',
  IMPERSONATE: 'Suplantación',
};

const FIELD_LABELS: Record<string, string> = {
  status: 'Estado',
  confirmedAt: 'Fecha de confirmación',
  confirmedBy: 'Confirmado por',
  cancelledAt: 'Fecha de cancelación',
  cancellationReason: 'Motivo de cancelación',
  cutoffOverridden: 'Excepción de corte',
  cutoffOverrideReason: 'Motivo de la excepción',
  totalQuantity: 'Cantidad total',
  subtotalCents: 'Subtotal',
  vatCents: 'IVA',
  totalCents: 'Total',
};

function formatDate(value: string): string {
  return new Date(value).toLocaleString('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/**
 * `orders/spec.md` → "Historial del pedido" (UI). `GET /orders/:id/history`
 * answers `AuditLog` rows newest first (`apps/api` visual review
 * follow-up); rendered as a simple timeline rather than a table, since a
 * `CREATE` entry's `changedFields` is every column of the row (the audit
 * extension has no "before" to diff against on insert) and a table column
 * would either truncate that list or blow out the layout.
 */
export function OrderHistoryTab({ orderId }: OrderHistoryTabProps): JSX.Element {
  const query = useQuery({
    queryKey: ['order-history', orderId],
    queryFn: () => getOrderHistory(orderId),
  });

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <Alert tone="danger" title="No pudimos cargar el historial">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  if (query.data.length === 0) {
    return <EmptyState icon={History} title="Todavía no hay movimientos registrados." />;
  }

  return (
    <ol className="flex flex-col gap-4">
      {query.data.map((entry) => (
        <li key={entry.id} className="flex flex-col gap-1 border-l-2 border-slate-200 pl-4">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium text-slate-900">{ACTION_LABELS[entry.action]}</span>
            <span className="text-sm text-slate-500">{formatDate(entry.createdAt)}</span>
          </div>
          <p className="text-sm text-slate-700">
            {entry.actorFullName ?? 'Sistema'}
            {entry.changedFields.length === 0
              ? ''
              : ` · ${entry.changedFields.map(fieldLabel).join(', ')}`}
          </p>
        </li>
      ))}
    </ol>
  );
}
